/**
 * PREVIEW STORE
 *
 * Zustand store for managing preview environment lifecycle per project.
 */

import { create } from "zustand";
import { api } from "@/lib/api";

export type PreviewStatus = "not_created" | "running" | "stopped" | "unknown";

export interface PreviewEnvironment {
  projectId: number;
  environmentName: string;
  exists: boolean;
  status: PreviewStatus;
  previewUrl: string;
  apiUrl: string;
  branch?: string;
  repoUrl?: string;
  runningContainers: number;
  message?: string;
}

function mapEnvironmentFromApi(data: any): PreviewEnvironment {
  return {
    projectId: data.project_id,
    environmentName: data.environment_name,
    exists: Boolean(data.exists),
    status: (data.status as PreviewStatus) || "unknown",
    previewUrl: data.preview_url || "",
    apiUrl: data.api_url || "",
    branch: data.branch || undefined,
    repoUrl: data.repo_url || undefined,
    runningContainers: data.running_containers || 0,
    message: data.message || undefined,
  };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Unknown preview error";
}

interface PreviewState {
  environments: Record<number, PreviewEnvironment | undefined>;
  loading: Record<number, boolean>;
  mutating: Record<number, boolean>;
  errors: Record<number, string | null>;

  fetchEnvironment: (projectId: number) => Promise<void>;
  startEnvironment: (projectId: number, branch?: string) => Promise<PreviewEnvironment>;
  stopEnvironment: (projectId: number) => Promise<PreviewEnvironment>;
  deleteEnvironment: (projectId: number) => Promise<PreviewEnvironment>;
  clearError: (projectId: number) => void;
}

export const usePreviewStore = create<PreviewState>()((set) => ({
  environments: {},
  loading: {},
  mutating: {},
  errors: {},

  fetchEnvironment: async (projectId: number) => {
    set((s) => ({
      loading: { ...s.loading, [projectId]: true },
      errors: { ...s.errors, [projectId]: null },
    }));
    try {
      const response = await api.get<any>(`/api/projects/${projectId}/preview`);
      const env = mapEnvironmentFromApi(response);
      set((s) => ({
        environments: { ...s.environments, [projectId]: env },
        loading: { ...s.loading, [projectId]: false },
      }));
    } catch (error) {
      set((s) => ({
        loading: { ...s.loading, [projectId]: false },
        errors: { ...s.errors, [projectId]: toErrorMessage(error) },
      }));
      throw error;
    }
  },

  startEnvironment: async (projectId: number, branch?: string) => {
    set((s) => ({
      mutating: { ...s.mutating, [projectId]: true },
      errors: { ...s.errors, [projectId]: null },
    }));
    try {
      const response = await api.post<any>(`/api/projects/${projectId}/preview/start`, {
        branch: branch || "main",
      });
      const env = mapEnvironmentFromApi(response);
      set((s) => ({
        environments: { ...s.environments, [projectId]: env },
        mutating: { ...s.mutating, [projectId]: false },
      }));
      return env;
    } catch (error) {
      set((s) => ({
        mutating: { ...s.mutating, [projectId]: false },
        errors: { ...s.errors, [projectId]: toErrorMessage(error) },
      }));
      throw error;
    }
  },

  stopEnvironment: async (projectId: number) => {
    set((s) => ({
      mutating: { ...s.mutating, [projectId]: true },
      errors: { ...s.errors, [projectId]: null },
    }));
    try {
      const response = await api.post<any>(`/api/projects/${projectId}/preview/stop`);
      const env = mapEnvironmentFromApi(response);
      set((s) => ({
        environments: { ...s.environments, [projectId]: env },
        mutating: { ...s.mutating, [projectId]: false },
      }));
      return env;
    } catch (error) {
      set((s) => ({
        mutating: { ...s.mutating, [projectId]: false },
        errors: { ...s.errors, [projectId]: toErrorMessage(error) },
      }));
      throw error;
    }
  },

  deleteEnvironment: async (projectId: number) => {
    set((s) => ({
      mutating: { ...s.mutating, [projectId]: true },
      errors: { ...s.errors, [projectId]: null },
    }));
    try {
      const response = await api.delete<any>(`/api/projects/${projectId}/preview`);
      const env = mapEnvironmentFromApi(response);
      set((s) => ({
        environments: { ...s.environments, [projectId]: env },
        mutating: { ...s.mutating, [projectId]: false },
      }));
      return env;
    } catch (error) {
      set((s) => ({
        mutating: { ...s.mutating, [projectId]: false },
        errors: { ...s.errors, [projectId]: toErrorMessage(error) },
      }));
      throw error;
    }
  },

  clearError: (projectId: number) => {
    set((s) => ({ errors: { ...s.errors, [projectId]: null } }));
  },
}));
